import { Button, Chip } from '@heroui/react';

import { Icon } from '../../various/Icon';

const ENTERPRISE_DEMO_URL = 'https://calendar.app.google/NXbAeTAUwaBGh5x49';

const TEAM_FEATURES = [
  'Seat the whole review circle — up to 10 members',
  '2 TB for full-res cuts, versions, and client packs',
  'Unlimited projects, guest review links, and frame-accurate comments',
  'Shared libraries so the next job starts organized',
];

const CREATOR_FEATURES = [
  'Up to 3 members and 500 GB storage',
  'Unlimited projects and reviewers',
  'Frame-accurate video review and guest review links',
  'Shared asset libraries',
];

interface TrialPlanCardProps {
  name: string;
  description: string;
  afterPrice: string;
  features: string[];
  ctaLabel: string;
  isLoading: boolean;
  isDisabled: boolean;
  emphasized?: boolean;
  onStart: () => void;
}

const TrialPlanCard = ({
  name,
  description,
  afterPrice,
  features,
  ctaLabel,
  isLoading,
  isDisabled,
  emphasized = false,
  onStart,
}: TrialPlanCardProps) => {
  return (
    <div
      className={`relative flex h-full flex-col rounded-2xl p-5 md:p-6 ${
        emphasized ? 'border-2 border-foreground bg-foreground-50 pt-7' : 'border border-foreground-200'
      }`}
    >
      <div className="flex items-center gap-2">
        <p className="font-sans text-base font-semibold text-foreground">{name}</p>
        {emphasized && (
          <Chip size="sm" className="bg-foreground text-content1">
            Recommended
          </Chip>
        )}
      </div>
      <p className="mt-1 text-sm text-foreground-600">{description}</p>
      <div className="mt-4 flex items-end gap-2">
        <span className="font-sans text-5xl font-semibold leading-none tracking-tight">$0</span>
        <span className="pb-1 text-sm text-foreground-500">today</span>
      </div>
      <p className="mt-2 text-sm text-foreground-500">{afterPrice}</p>

      <ul className="mt-5 flex flex-1 flex-col gap-2.5">
        {features.map((label) => (
          <li key={label} className="flex items-start gap-2 text-sm text-foreground">
            <Icon icon="check" size={16} className="mt-0.5 shrink-0 text-foreground" />
            <span className={emphasized ? 'font-medium' : undefined}>{label}</span>
          </li>
        ))}
      </ul>

      <Button
        className={`mt-6 h-11 w-full ${emphasized ? 'bg-foreground text-content1' : ''}`}
        variant={emphasized ? 'solid' : 'bordered'}
        isLoading={isLoading}
        isDisabled={isDisabled}
        onPress={onStart}
      >
        {ctaLabel}
      </Button>
    </div>
  );
};

interface Props {
  isCreatorLoading: boolean;
  isTeamLoading: boolean;
  onStartCreator: () => void;
  onStartTeam: () => void;
}

export const TrialStartPanel = ({ isCreatorLoading, isTeamLoading, onStartCreator, onStartTeam }: Props) => {
  const isBusy = isCreatorLoading || isTeamLoading;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 md:grid-cols-2 md:items-stretch">
        <TrialPlanCard
          name="Team"
          description="For studios running client reviews with a crew."
          afterPrice="$29 / user / month after day 7"
          features={TEAM_FEATURES}
          ctaLabel="Start Team trial"
          isLoading={isTeamLoading}
          isDisabled={isBusy && !isTeamLoading}
          emphasized
          onStart={onStartTeam}
        />
        <TrialPlanCard
          name="Creator"
          description="For solo work and tiny crews of up to 3."
          afterPrice="$14 / user / month after day 7"
          features={CREATOR_FEATURES}
          ctaLabel="Start Creator trial"
          isLoading={isCreatorLoading}
          isDisabled={isBusy && !isCreatorLoading}
          onStart={onStartCreator}
        />
      </div>
      <p className="text-center text-xs text-foreground-500">Add a card · not charged until day 8 · cancel anytime</p>
      <a
        href={ENTERPRISE_DEMO_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="text-center text-sm text-foreground-500 underline-offset-2 hover:text-foreground hover:underline"
      >
        Enterprise? Book a demo
      </a>
    </div>
  );
};
